import { z } from "zod";
import { ATS_STAGE_TYPES } from "@/app/lib/constants";

// ---------------------------------------------------------------------------
// ATS Pipeline & Stage Validators
//
// Zod schemas that validate incoming HTTP request bodies before they reach the
// service layer. Used in API routes via safeParse().
//
// Pattern: Same as atsJobRequisitionValidator.ts, atsNotesValidator.ts,
//          atsFormValidator.ts, atsShortLinkValidator.ts — export Zod schemas,
//          use safeParse() in the route handler, return the first error on failure.
// ---------------------------------------------------------------------------

// Valid stage type values — derived from the ATS_STAGE_TYPES constant so there's
// a single source of truth. If a new stage type is added to the constant, this
// validator automatically accepts it without any changes here.
const STAGE_TYPE_VALUES = Object.values(ATS_STAGE_TYPES) as [string, ...string[]];

// -----------------------------------------------------------------------------
// Create Pipeline Validator
//
// USED BY: POST handler in api/ats/pipelines/route.ts
//
// LOGIC: A pipeline is the named container for stages (e.g. "DA Standard (13 stages)").
//        Only name is required — description and serviceAreaId are optional because
//        a pipeline can be org-wide (no service area) or scoped to a specific station.
//        serviceAreaId is a text field (not UUID) because it references serviceAreas.serviceAreaId
//        which is a text primary key, not a UUID — see schema.ts serviceAreas table.
// -----------------------------------------------------------------------------
export const createPipelineValidator = z.object({
  name: z.string().min(1, "Pipeline name is required"),
  description: z.string().optional(),
  serviceAreaId: z.string().optional(),
});

// -----------------------------------------------------------------------------
// Update Pipeline Validator
//
// USED BY: PATCH handler in api/ats/pipelines/[id]/route.ts
//
// LOGIC: All fields optional (partial update) — the recruiter may only want to
//        change the name, or only the description. The service layer checks for
//        duplicate names if name is being changed. serviceAreaId is included
//        here (unlike job requisitions where it's immutable) because a pipeline's
//        service area scope can change — it's a template, not a live record.
// -----------------------------------------------------------------------------
export const updatePipelineValidator = z.object({
  name: z.string().min(1, "Pipeline name cannot be empty").optional(),
  description: z.string().optional(),
  serviceAreaId: z.string().optional(),
});

// -----------------------------------------------------------------------------
// Create Stage Validator
//
// USED BY: POST handler in api/ats/pipelines/[id]/stages/route.ts
//
// LOGIC: A stage is one step in the hiring funnel (e.g. "Screening", "Interview").
//        - name: required, shown on the board column header + portal.
//        - type: required, determines which service + UI component handles the stage.
//          Validated against ATS_STAGE_TYPES so invalid types are rejected at the
//          boundary (e.g. "foo" → 400 error, not a runtime crash in the portal).
//        - displayOrder: optional — if omitted, the service computes it as
//          (max existing displayOrder + 1) so new stages appear at the end.
//        - slaHours: nullable — null means no SLA (the stage never times out).
//          Uses .nullish() (not .optional()) because the frontend sends null
//          (not undefined) when the field is left empty. .optional() only
//          accepts undefined, not null — so .nullish() is required to accept
//          both null (no SLA) and undefined (field omitted).
//        - isActive: optional, defaults to true. Set false to feature-flag a stage
//          off (e.g. Video Screening is Phase 2, isActive=false).
//        - config: nullable JSONB — shape varies by type (form config, offer template
//          ID, interview config, etc.). Typed as a generic record to stay flexible.
//          Uses .nullish() (not .optional()) because the frontend sends null
//          (not undefined) when the config textarea/dropdown is left empty.
//          null = "no config for this stage", undefined = "field omitted".
//        - serviceAreaId: optional — inherited from the parent pipeline if not provided.
// -----------------------------------------------------------------------------
export const createStageValidator = z.object({
  name: z.string().min(1, "Stage name is required"),
  type: z.enum(STAGE_TYPE_VALUES, {
    errorMap: () => ({ message: `Stage type must be one of: ${STAGE_TYPE_VALUES.join(", ")}` }),
  }),
  displayOrder: z.number().int().min(0).optional(),
  slaHours: z.number().int().min(0).nullish(),
  isActive: z.boolean().optional(),
  config: z.record(z.unknown()).nullish(),
  serviceAreaId: z.string().optional(),
});

// -----------------------------------------------------------------------------
// Update Stage Validator
//
// USED BY: PATCH handler in api/ats/pipelines/[id]/stages/[stageId]/route.ts
//
// LOGIC: All fields optional (partial update). The recruiter may only want to
//        rename a stage or change its SLA. displayOrder is intentionally NOT
//        included here — reordering is a separate operation (reorderStagesValidator)
//        to avoid two competing updates to displayOrder on the same stage.
//        type is included so a stage's engine can be changed (e.g. upgrading a
//        manual stage to a scheduler stage once the interview service is built).
// -----------------------------------------------------------------------------
export const updateStageValidator = z.object({
  name: z.string().min(1, "Stage name cannot be empty").optional(),
  type: z.enum(STAGE_TYPE_VALUES, {
    errorMap: () => ({ message: `Stage type must be one of: ${STAGE_TYPE_VALUES.join(", ")}` }),
  }).optional(),
  // .nullish() (not .optional()) — accepts null (clear the SLA) and undefined
  // (don't change it). The frontend sends null when the field is cleared.
  slaHours: z.number().int().min(0).nullish(),
  isActive: z.boolean().optional(),
  // .nullish() (not .optional()) — accepts null (clear the config) and undefined
  // (don't change it). The frontend sends null when the config is emptied.
  config: z.record(z.unknown()).nullish(),
  serviceAreaId: z.string().optional(),
});

// -----------------------------------------------------------------------------
// Reorder Stages Validator
//
// USED BY: PATCH handler in api/ats/pipelines/[id]/stages/reorder/route.ts
//
// LOGIC: Takes an ordered array of stage UUIDs representing the desired new order.
//        This is the simplest contract for a drag-to-reorder UI — after the user
//        drags a stage to a new position, the frontend sends the full list of stage
//        IDs in the new order. The backend validates that the set of IDs matches
//        the pipeline's actual stages (no missing, no extra, no foreign stages)
//        and then applies the new displayOrder values.
//
//        Why an array instead of patch operations (e.g. "move stage X before Y")?
//        Array-of-IDs is idempotent, atomic in intent, and trivial to implement on
//        both frontend and backend. Patch operations require conflict resolution
//        and are harder to reason about. The max pipeline size is ~13 stages, so
//        sending the full list is negligible payload.
// -----------------------------------------------------------------------------
export const reorderStagesValidator = z.object({
  orderedStageIds: z
    .array(z.string().uuid("Each stage ID must be a valid UUID"))
    .min(1, "At least one stage ID is required"),
});

// -----------------------------------------------------------------------------
// Follow-up Rule Validator
// -----------------------------------------------------------------------------
// WHAT: A single follow-up rule stored inside `stage.config.followUpRules.rules`.
// WHY:  Rules are JSONB, so malformed rows (wrong types, missing fields, bad
//       delay values) must be filtered before they enter React state or the
//       manual send flow. Using a Zod schema keeps the shape in one place and
//       makes adding new fields a one-line change. .passthrough() allows extra
//       keys so future optional fields don't break existing rules.
// -----------------------------------------------------------------------------
export const followUpRuleSchema = z
  .object({
    id: z.string().min(1, "Follow-up rule id is required"),
    templateId: z.string(),
    delayDays: z.number().int().min(0, "delayDays must be a non-negative integer"),
    enabled: z.boolean(),
  })
  .passthrough();
